/**
 * Puts a server 400's messages under the dialog fields they are about, so a
 * rule the client does not check still lands under its field rather than in a
 * toast [HW-1008]. NestJS's ValidationPipe answers `{ message: string[] }`;
 * class-validator's own messages start with the property name ("fopName must
 * be shorter than…"), the DTOs' custom ones with the field's label ("FOP name
 * is required"), so each field lists the prefixes that name it — each ending
 * in a space, so `kved ` does not claim "kvedCodes …".
 */

export interface ServerFieldErrors<K extends string> {
  /** The first message for each field that has one. */
  fields: Partial<Record<K, string>>;
  /** Messages that name no field — for a toast. */
  unmatched: string[];
}

/** null when `error` is not a 400 carrying messages; show it as a toast then. */
export function serverFieldErrors<K extends string>(
  error: unknown,
  prefixes: Record<K, readonly string[]>
): ServerFieldErrors<K> | null {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
    ?.response;
  if (response?.status !== 400) return null;
  const raw = response.data?.message;
  const messages = (Array.isArray(raw) ? raw : [raw]).filter(
    (m): m is string => typeof m === 'string' && m.trim().length > 0
  );
  if (messages.length === 0) return null;

  const fields: Partial<Record<K, string>> = {};
  const unmatched: string[] = [];
  const keys = Object.keys(prefixes) as K[];
  for (const message of messages) {
    const field = keys.find((k) => prefixes[k].some((p) => message.startsWith(p)));
    if (!field) unmatched.push(message);
    else if (!fields[field]) fields[field] = message;
  }
  return { fields, unmatched };
}
